import React from 'react';
import { useActor } from '@xstate/react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import Yup from 'yup';

import { InformedOTPField } from '@nw/design-system/omega';
import { otpFieldConstants, otpFieldTypeConstants } from '@nw/shared/config';
import { OTPFieldWithResend } from '@nw/shared/ui';

import { PublicFormMachineStateType } from '../../../form/machines/PublicFormsMachine/types';
//TODO: Use product types from shared
import { PublicProductFormMachineStateType } from '../../../product/machines/PublicFormsMachine/types';

import InformedOTPFieldModel from '../../stores/models/FieldModels/InformedOTPfield';

import { InformedFieldsInfoInterface } from '../RenderAllFormFields';
import {
  errorMessageStyles,
  labelStyles,
  OTPBoxesFieldContainerStyles,
  OTPContainerStyles
} from '../RenderAllFormFields/styledComponent';

interface OTPFieldBasedOnTypeProps {
  fieldData: InformedOTPFieldModel;
  registrationFormState:
  | PublicProductFormMachineStateType
  | PublicFormMachineStateType;
  authMachineRef: any;
  validationSchemaObject: Yup.AnyObjectSchema;
  informedFieldsInfo: InformedFieldsInfoInterface;
  onSuccessOtpVerified?: () => void;
  enableAutoVerifyForSixDigitOTP?: boolean;
}

const OTPFieldBasedOnType: React.FC<OTPFieldBasedOnTypeProps> = ({
  fieldData,
  registrationFormState,
  authMachineRef,
  validationSchemaObject,
  informedFieldsInfo,
  onSuccessOtpVerified,
  enableAutoVerifyForSixDigitOTP = false
}) => {
  const { t } = useTranslation();

  const { otpInputType } = fieldData;
  const { sixDigitInputBoxes } = otpFieldTypeConstants;

  const [authCoreMachineState, authCoreMachineSend] = useActor(authMachineRef);

  const { touched, errors } = informedFieldsInfo;

  const renderInformedOTPField = (
    fieldData: InformedOTPFieldModel,
    authCoreMachineState: any
  ) => {
    const { field, label, type, placeholder, shouldShowLabel } = fieldData;
    const { touched, errors } = informedFieldsInfo;
    return (
      <InformedOTPField
        fieldType={type}
        field={field}
        key={field}
        label={label}
        error={errors[field]}
        touched={touched[field]}
        shouldShowIcon={false}
        validationSchemaObject={validationSchemaObject}
        isDisabled={registrationFormState.context.isLoggedIn}
        registrationFormState={authCoreMachineState}
        placeholder={placeholder}
        isVerifyingOtp={registrationFormState.hasTag('verifyingOtp')}
        validateOnBlur={false}
        validateOnChange={false}
        labelStyles={
          shouldShowLabel
            ? cn('mb-1 otp-label-styles')
            : cn('mb-1 hidden otp-label-styles')
        }
        shouldShowLabel={shouldShowLabel}
        onSuccessOtpVerified={onSuccessOtpVerified}
        isOtpAutofillEnabled={Boolean(
          (registrationFormState as PublicFormMachineStateType).context.formInfo
            ?.formConfig?.enableOtpAutofill
        )}
      />
    );
  };

  const renderSixDigitOTPFieldWithResend = (
    fieldData: InformedOTPFieldModel,
    authCoreMachineState: any,
    enableAutoVerify: boolean
  ) => {
    const { field } = fieldData;
    return (
      <OTPFieldWithResend
        field={field}
        label={t('common.oneTimePasscode')}
        validationSchemaObject={validationSchemaObject}
        state={authCoreMachineState}
        formState={informedFieldsInfo}
        labelStyles={labelStyles}
        errorMessageStyles={errorMessageStyles}
        resendOtpButtonWithTimerVariant={
          otpFieldConstants.timerAwayFromResendOtp
        }
        OTPBoxesFieldContainerStyles={OTPBoxesFieldContainerStyles}
        otpContainerStyles={OTPContainerStyles}
        enableAutoVerify={enableAutoVerify}
      />
    );
  };

  const renderOtpFieldBasedOnType = (authCoreMachineState: any) => {
    if (!authCoreMachineState.context.otpFieldRef) return null;
    switch (otpInputType) {
      case sixDigitInputBoxes:
        return renderSixDigitOTPFieldWithResend(
          fieldData,
          authCoreMachineState,
          enableAutoVerifyForSixDigitOTP
        );
      default:
        return renderInformedOTPField(fieldData, authCoreMachineState);
    }
  };

  return <> {renderOtpFieldBasedOnType(authCoreMachineState)} </>;
};

export default OTPFieldBasedOnType;
